import type { RestaurantDemoState } from "./restaurant-sandbox";
import type { RestaurantDemoV5State } from "./restaurant-demo-v5-state";

export interface RestaurantAgentV7HistoryMessage {
  readonly role: "customer" | "assistant";
  readonly text: string;
}

export interface RestaurantAgentV7Action {
  readonly capability: string;
  readonly label: string;
  readonly detail: string;
  readonly status: "succeeded" | "failed" | "waiting_approval";
}

export interface RestaurantAgentV7ProposalItem {
  readonly label: string;
  readonly before?: string;
  readonly after?: string;
  readonly detail?: string;
}

export interface RestaurantAgentV7Proposal {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly risk: "sensitive";
  readonly operation: {
    readonly operation: string;
    readonly arguments: Readonly<Record<string, unknown>>;
  };
  readonly items: readonly RestaurantAgentV7ProposalItem[];
  readonly snapshotHash: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface RestaurantAgentV7QuickChoice {
  readonly id: string;
  readonly label: string;
  readonly message: string;
}

export interface RestaurantAgentV7PlanStep {
  readonly id: string;
  readonly label: string;
  readonly operation: string;
  readonly status: "succeeded" | "failed" | "waiting_approval" | "blocked";
  readonly detail: string;
  readonly errorCode?: string;
  readonly proposalId?: string;
}

export interface RestaurantAgentV7Plan {
  readonly id: string;
  readonly goal: string;
  readonly status: "completed" | "partial" | "waiting_approval" | "failed";
  readonly steps: readonly RestaurantAgentV7PlanStep[];
}

export interface RestaurantAgentV7PlanContextStep {
  readonly id: string;
  readonly label?: string;
  readonly operation: string;
  readonly arguments: Readonly<Record<string, unknown>>;
  readonly dependsOn?: readonly string[];
}

export interface RestaurantAgentV7PlanContext {
  readonly goal: string;
  readonly steps: readonly RestaurantAgentV7PlanContextStep[];
}

export interface RestaurantAgentV7Result {
  readonly response: string;
  readonly actions: readonly RestaurantAgentV7Action[];
  readonly proposals: readonly RestaurantAgentV7Proposal[];
  readonly plan: RestaurantAgentV7Plan | null;
  readonly planContext: RestaurantAgentV7PlanContext | null;
  readonly quickChoices: readonly RestaurantAgentV7QuickChoice[];
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly runtime: {
    readonly mode: "real" | "fallback";
    readonly providerKey: string | null;
    readonly model: string | null;
    readonly rounds: number;
    readonly steps: number;
    readonly terminalReason: string;
  };
}

export interface RestaurantAgentV7ApprovalResult {
  readonly action: RestaurantAgentV7Action;
  readonly continuationActions: readonly RestaurantAgentV7Action[];
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly plan: RestaurantAgentV7Plan | null;
  readonly planContext: RestaurantAgentV7PlanContext | null;
  readonly proposals: readonly RestaurantAgentV7Proposal[];
  readonly quickChoices: readonly RestaurantAgentV7QuickChoice[];
  readonly output?: unknown;
}

export class RestaurantAgentV7ClientError extends Error {
  constructor(readonly code: string, message: string, readonly status?: number) {
    super(message);
    this.name = "RestaurantAgentV7ClientError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function parseResponse(response: Response): Promise<Record<string, unknown>> {
  const raw = await response.text();
  let payload: unknown;
  try { payload = JSON.parse(raw) as unknown; }
  catch { throw new RestaurantAgentV7ClientError("invalid_response", `Agent Runtime devolvió HTTP ${response.status} sin JSON válido.`, response.status); }
  if (!isRecord(payload)) throw new RestaurantAgentV7ClientError("invalid_response", "Agent Runtime devolvió una respuesta inválida.", response.status);
  if (!response.ok) {
    const error = isRecord(payload.error) ? payload.error : {};
    throw new RestaurantAgentV7ClientError(typeof error.code === "string" ? error.code : `http_${response.status}`, typeof error.message === "string" ? error.message : "Agent Runtime no está disponible.", response.status);
  }
  return payload;
}

export async function runRestaurantAgentV7(input: {
  readonly message: string;
  readonly history: readonly RestaurantAgentV7HistoryMessage[];
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly activePlan?: RestaurantAgentV7Plan | null;
  readonly activePlanContext?: RestaurantAgentV7PlanContext | null;
}): Promise<RestaurantAgentV7Result> {
  const response = await fetch("/api/v1/platform/sales-demo/restaurant-agent-v7/turn", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      message: input.message,
      history: input.history,
      state: input.state,
      v5: input.v5,
      activePlan: input.activePlan ?? null,
      activePlanContext: input.activePlanContext ?? null,
    }),
  });
  const payload = await parseResponse(response);
  if (!isRecord(payload.data)) throw new RestaurantAgentV7ClientError("invalid_response", "Agent Runtime no devolvió data.", response.status);
  return payload.data as unknown as RestaurantAgentV7Result;
}

export async function resolveRestaurantAgentV7Proposal(input: {
  readonly decision: "apply" | "reject";
  readonly proposal: RestaurantAgentV7Proposal;
  readonly state: RestaurantDemoState;
  readonly v5: RestaurantDemoV5State;
  readonly plan?: RestaurantAgentV7Plan | null;
  readonly planContext?: RestaurantAgentV7PlanContext | null;
}): Promise<RestaurantAgentV7ApprovalResult> {
  const response = await fetch("/api/v1/platform/sales-demo/restaurant-agent-v7/approval", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      decision: input.decision,
      proposal: input.proposal,
      state: input.state,
      v5: input.v5,
      plan: input.plan ?? null,
      planContext: input.planContext ?? null,
    }),
  });
  const payload = await parseResponse(response);
  if (!isRecord(payload.data)) throw new RestaurantAgentV7ClientError("invalid_response", "Approval Engine no devolvió data.", response.status);
  return payload.data as unknown as RestaurantAgentV7ApprovalResult;
}
